/* eslint-disable no-self-assign */
// React imports
import { useState, useMemo, useEffect } from 'react';
import { useOutletContext, useNavigate, useLocation, useSearchParams } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import pluralize from 'pluralize';
import axios from 'axios';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { grey } from '@mui/material/colors';
import CircularProgress from '@mui/material/CircularProgress';

// Font Awesome imports
import { faBullseyeArrow, faPlus, faSquareList, faSquareKanban } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Local imports
import Header from '../../Header.js';
import TradelinesFilter, { applyFilter } from './TradelinesFilter.js';
import TradelineRow from './TradelineRow.js';
import TradelinesPageKanban from './TradelinesPage.kanban.js';
import { getTradelinesStatus } from './utils.js';

function TradelinesPage(props) {
    const { org, tradelines, onUpdateTradeline, opportunities, exchangeRates, me, accounts, accesstoken } = useOutletContext();

    const filterStorage = sessionStorage.getItem('tradelines.filter');
    const baseFilter = (() => {
        const base = { teamMember: me.email, tradelineType: 'all', tradelineStatus: 'all', accounts: [], tags: [], freetext: '', transportationMethods: [] };
        try {
            return filterStorage ? JSON.parse(filterStorage) : base;
        } catch (e) {
            return base;
        }
    })();
    const [ filter, setFilter ] = useState(baseFilter);
    const [ view, setView ] = useState(sessionStorage.getItem('tradelines.view') || 'list');

    const [pageNumber, setPageNumber] = useState(1);
    const navigate = useNavigate();
    const location = useLocation();

    const filteredTradelines = (tradelines || []).filter(r => r.status !== 'deleted');

    function _setView(view) {
        sessionStorage.setItem('tradelines.view', view);
        setView(view);
    }

    const handleScroll = () => {
        const bottom =
            window.innerHeight + window.scrollY >= document.body.offsetHeight - 20;
        if (bottom) {
            setPageNumber(prevPageNumber => {
                if (prevPageNumber * 20 > filteredTradelines.length) return prevPageNumber;
                return prevPageNumber + 1
            });
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [ tradelines ]);

    if (!tradelines || !accounts || !opportunities) {
        return (
            <Box sx={{ padding: '36px 0px', background: '#fafbfc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    function setFilterWithStorage(newFilter) {
        sessionStorage.setItem('tradelines.filter', JSON.stringify(newFilter));
        setFilter(newFilter);
    }

    function onOpen({ tradeline, newWindow }) {
        if (newWindow) {
            if (!location.pathname || location.pathname.length < 2) {
                window.open(`https://app.monada.ai/organization/${org.alias}/tradelines/${tradeline.id}/?accesstoken=${encodeURIComponent(accesstoken.monada)}`, '_blank');
            } else {
                window.open(`${location.pathname.replace(/\/main/, '')}/${tradeline.id}/?${location.search}`.replace(/\/\//g, '/'), '_blank');
            }
        } else {
            navigate(`/organization/${org.alias}/tradelines/${tradeline.id}/`);
        }
    }

    async function updateTradeline({ tradeline, setFirst }) {
        if (setFirst) {
            onUpdateTradeline({ tradeline });
        }

        let _tradeline = _.omit(tradeline, ['log' ]);
        _tradeline.updates = _tradeline.updates?.map(u => !u.pendingDelete ? u : {..._.omit(u, 'pendingDelete'), deleted: true });
        _tradeline.updates = _tradeline.updates?.map(u => !u.editHtml ? u : {..._.omit(u, 'editHtml', 'editTags'), html: u.editHtml, tags: u.editTags });
        _tradeline.updates = _tradeline.updates?.map(u => _.omit(u, 'wasEdited'));

        const { data } = await axios.put(`/api/tradelines/${tradeline.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline: _tradeline });
        onUpdateTradeline({ tradeline: data });
    }

    async function onSendTradelineComment({ html, tags, files }) {
        console.log('@@@@@@@@@@@@@@@@@@ TODO');
    }

    const mainTradelines = _.sortBy(applyFilter({ filter, tradelines: filteredTradelines, org, accounts }), r => -1 * r.timespan?.end);

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faBullseyeArrow}
                title={'My Sales Targets'}
                buttons={[{
                    title: 'New Sales Target',
                    icon: faPlus,
                    color: 'monada-button',
                    action: () => {
                        navigate(`/organization/${org.alias}/tradelines/new/`);
                    }
                }]}
            />
            <Box style={{ padding: '12px', paddingTop: '0px' }}>

                <TradelinesFilter
                    filter={filter}
                    setFilter={setFilterWithStorage}
                    me={me}
                    tradelines={filteredTradelines}
                    accounts={accounts}
                    org={org}
                />

                {(mainTradelines.length === 0) && (filter.teamMember === me.email) && (
                    <Box sx={{ paddingTop: '24px', textAlign: 'center' }}>
                        <Typography variant='body1'>
                            You don't have any sales targets.
                        </Typography>
                        <Typography variant='body1' sx={{ marginTop: '12px' }}>
                            Create Your First Sales Target
                        </Typography>
                    </Box>
                )}
                {(mainTradelines.length > 0) && (
                    <Box>
                        <Paper sx={{ marginTop: '12px', border: `1px solid ${grey[300]}` }} elevation={0}>
                            <Typography variant='body2' sx={{ fontWeight: 800, padding: '12px' }}>
                                Sales Targets
                            </Typography>
                            <div style={{ padding: '6px 12px', paddingTop: '0px' }}>
                                {(() => {
                                    const stats = mainTradelines.map(t => {
                                        const s = getTradelinesStatus({ opportunities, exchangeRates, tradeline: t, organization: org });
                                        return s;
                                    });
                                    const running = stats.filter(s => s.aggregated.status === 'inprogress').length;
                                    const completed = stats.filter(s => s.aggregated.status === 'completed').length;
                                    const failed = stats.filter(s => s.aggregated.status === 'failed').length;
                                    const totalWon = _.sumBy(stats, s => s.wonOpportunities?.length || 0);
                                    const totalRevenue = _.sumBy(stats, s => s.accumulatedValues?.totalRevenue || 0);
                                    const totalProfit = _.sumBy(stats, s => s.accumulatedValues?.totalProfit || 0);
                                    const cur = org.defaults?.currency || 'USD';
                                    const fmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                                    return (
                                        <>
                                            <Typography variant='caption'>
                                                Total of <b>{mainTradelines.length.toLocaleString()} {pluralize('target', mainTradelines.length)}</b> | Running: <b>{running}</b> | Completed: <b>{completed}</b> | Failed: <b>{failed}</b>
                                            </Typography>
                                            <Typography variant='caption' component='div' sx={{ mt: '2px' }}>
                                                Won shipments: <b>{totalWon}</b> | Revenue: <b>{cur} {fmt.format(totalRevenue)}</b> | Profit: <b style={{ color: totalProfit >= 0 ? '#2e7d32' : '#d32f2f' }}>{cur} {fmt.format(totalProfit)}</b>
                                            </Typography>
                                        </>
                                    );
                                })()}
                                <Box sx={{ display: 'flex', gap: '12px', mt: '6px', alignItems: 'center' }}>
                                    <Box sx={{ flex: 1 }} />
                                    <Button
                                        variant='text'
                                        color='primary'
                                        onClick={() => _setView('list')}
                                        sx={{ minWidth: 0, padding: 0, textTransform: 'none', fontSize: '11px !important', fontWeight: view === 'list' ? 800 : 400 }}
                                    >
                                        <FontAwesomeIcon icon={faSquareList} />&nbsp;List
                                    </Button>
                                    <Typography variant='caption' sx={{ fontSize: '11px' }}>/</Typography>
                                    <Button
                                        variant='text'
                                        color='primary'
                                        onClick={() => _setView('kanban')}
                                        sx={{ minWidth: 0, padding: 0, textTransform: 'none', fontSize: '11px !important', fontWeight: view === 'kanban' ? 800 : 400 }}
                                    >
                                        <FontAwesomeIcon icon={faSquareKanban} />&nbsp;Kanban
                                    </Button>
                                </Box>
                            </div>
                        </Paper>
                        {view === 'list' && (
                            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
                                {mainTradelines.slice(0, pageNumber * 20).map((tradeline, i) => (
                                    <TradelineRow
                                        key={tradeline.id}
                                        tradeline={tradeline}
                                        onOpen={onOpen}
                                        opportunities={opportunities}
                                        exchangeRates={exchangeRates}
                                        accounts={accounts}
                                        onSendUpdate={onSendTradelineComment}
                                        tradelines={tradelines}
                                        organization={org}
                                    />
                                ))}
                            </Box>
                        )}
                        {view === 'kanban' && (
                            <TradelinesPageKanban
                                tradelines={mainTradelines}
                                opportunities={opportunities}
                                exchangeRates={exchangeRates}
                                onOpen={onOpen}
                                updateTradeline={updateTradeline}
                                accounts={accounts}
                                organization={org}
                                me={me}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default TradelinesPage;